import { supabase } from '../../lib/supabase'
import type { BoardOverrideEntry } from './planningBoardDraftStore'
import { cellKey, parseCellKey, type Period } from './planningBoardCellKey'

/**
 * The real counterpart to planningBoardPublicationStore.ts, which this
 * file replaces: publication now lives in muster.schedule_versions
 * (20260924010000_schedule_publication.sql), written only through
 * v1-publish-schedule, not sessionStorage. Same public shape
 * (PublicationVersion, getPublications, publishBoard, overridesMatch) so
 * SupervisorPlanningBoardPage.tsx's own call sites change as little as
 * possible.
 */
export type PublicationVersion = {
  version: number
  publishedBy: string
  publishedAt: string
  overrides: BoardOverrideEntry[]
}

type ScheduleVersionRow = {
  version: number
  published_by_name: string
  published_at: string
  entries: { employeeId: string; employeeName: string; date: string; category: BoardOverrideEntry['category']; label: string; detail?: string }[]
}

// Every published version of one team's one pay period, oldest first
// (the same order publishedVersions' own array already assumed when
// sessionStorage held it), each version's own snapshot re-keyed from
// employeeId+date back into the board's own cellKey by matching each
// entry's date against `dates`. An entry naming a date outside `dates`
// (a period boundary shifted since publish, which this pilot's own
// clock-anchored periods never actually do) is dropped rather than
// producing a cellKey no column on the current board can ever select.
export async function getPublications(
  teamName: string,
  period: Period,
  periodStart: string,
  dates: { date: string }[],
): Promise<PublicationVersion[]> {
  const { data: team } = await supabase.from('teams').select('id').eq('name', teamName).maybeSingle()
  if (!team) return []

  const { data, error } = await supabase
    .from('schedule_versions')
    .select('version, published_by_name, published_at, entries')
    .eq('team_id', team.id)
    .eq('period_start', periodStart)
    .order('version', { ascending: true })
  if (error || !data) return []

  return (data as unknown as ScheduleVersionRow[]).map((row) => ({
    version: row.version,
    publishedBy: row.published_by_name,
    publishedAt: row.published_at,
    overrides: row.entries.flatMap((entry) => {
      const col = dates.findIndex((date) => date.date === entry.date)
      if (col === -1 || !entry.employeeId) return []
      return [{ key: cellKey(period, entry.employeeId, col), category: entry.category, label: entry.label, detail: entry.detail }]
    }),
  }))
}

export type WireEntry = {
  employeeId: string
  employeeName: string
  date: string
  category: BoardOverrideEntry['category']
  label: string
  detail?: string
}

// The board's cells as v1-publish-schedule takes them, re-keyed from the
// board's own cellKey (period|id|column) to employeeId+date: a snapshot has
// to outlive the specific column layout that produced it. A day with nothing
// scheduled has no entry to write, since it is the absence of one, so it is
// not sent.
export function wireEntriesFor(entries: BoardOverrideEntry[], dates: { date: string }[], rows: { id: string; name: string }[]): WireEntry[] {
  return entries.flatMap((entry) => {
    const { employeeId, col } = parseCellKey(entry.key)
    const date = dates[col]
    const employeeName = rows.find((member) => member.id === employeeId)?.name
    if (!date || !employeeName || entry.category === 'unscheduled') return []
    return [{ employeeId, employeeName, date: date.date, category: entry.category, label: entry.label, detail: entry.detail }]
  })
}

export type PublishedVersionMeta = { version: number; publishedBy: string; publishedAt: string }

export type PublishResult = { ok: true; meta: PublishedVersionMeta } | { ok: false; message: string | null }

// Returns the new version's own metadata on success, or the reason it
// failed: the caller needs to know before it confirms a publication that
// did not actually happen. No `overrides` here: the caller already holds the exact
// BoardOverrideEntry[] it just sent (effectivePeriodEntries' own
// result), the same value this call's own `entries` is built from, so
// echoing it back re-keyed would only be a second, redundant copy.
// `entries` is that same array, re-keyed to employeeId+date, since a
// snapshot has to outlive the specific column layout that produced it.
export async function publishBoard(
  teamName: string,
  periodStart: string,
  periodEnd: string,
  entries: WireEntry[],
): Promise<PublishResult> {
  const { data, error } = await supabase.functions.invoke('v1-publish-schedule', {
    body: { teamName, periodStart, periodEnd, entries },
  })
  if (error || !data) {
    // The server's own reason, when it gave one, so the screen can say what to fix.
    const context = (error as { context?: { json?: () => Promise<{ error?: string }> } } | null)?.context
    try {
      const body = await context?.json?.()
      return { ok: false, message: body?.error ?? null }
    } catch {
      return { ok: false, message: null }
    }
  }
  const version = data.scheduleVersion as ScheduleVersionRow
  return { ok: true, meta: { version: version.version, publishedBy: version.published_by_name, publishedAt: version.published_at } }
}

// The pay periods, by start date, this team has published and so locked. A
// period stays locked until the supervisor reopens it.
export async function getPublishedPeriods(teamId: string): Promise<Set<string>> {
  const { data, error } = await supabase.from('published_periods').select('period_start').eq('team_id', teamId)
  if (error || !data) return new Set()
  return new Set((data as { period_start: string }[]).map((row) => row.period_start))
}

export type ReopenResult = { ok: true } | { ok: false; message: string | null }

// Reopens a published pay period: the board can be edited again and the team's
// agents can submit into it again, until it is published once more.
export async function reopenPayPeriod(teamName: string, periodStart: string): Promise<ReopenResult> {
  const { error } = await supabase.functions.invoke('v1-reopen-pay-period', { body: { teamName, periodStart } })
  if (!error) return { ok: true }
  const context = (error as { context?: { json?: () => Promise<{ error?: string }> } }).context
  try {
    const body = await context?.json?.()
    return { ok: false, message: body?.error ?? null }
  } catch {
    return { ok: false, message: null }
  }
}

// Order-independent: a period's overrides come out of a Map, whose
// iteration order is insertion order rather than anything meaningful to
// compare by, so two snapshots with the same entries in a different order
// must still read as unchanged.
export function overridesMatch(a: BoardOverrideEntry[], b: BoardOverrideEntry[]): boolean {
  if (a.length !== b.length) return false
  const sortedA = [...a].sort((x, y) => x.key.localeCompare(y.key))
  const sortedB = [...b].sort((x, y) => x.key.localeCompare(y.key))
  return sortedA.every((entry, index) => {
    const other = sortedB[index]
    return (
      entry.key === other.key &&
      entry.category === other.category &&
      entry.label === other.label &&
      entry.detail === other.detail
    )
  })
}
